import type { SolicitacaoAssistencia } from '@pgcustom/shared-types';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { PortalParceiroApi } from './portal-parceiro.api.js';

/**
 * `<erp-portal-solicitar-assistencia-modal>` — parceiro relata um
 * problema com um controle e envia para o Admin analisar. Mesmo padrão
 * de `<erp-configuracao-garantia-modal>`. `parceiroId` NUNCA é um campo
 * deste formulário — a API do portal sempre usa a sessão autenticada
 * (`PortalParceiroApi.solicitarAssistencia`, `assistencia.service.ts`
 * no backend).
 */
export class ErpPortalSolicitarAssistenciaModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _portalApi: PortalParceiroApi | null = null;
  private _aoEnviar: ((solicitacao: SolicitacaoAssistencia) => void) | null = null;

  private aberto = false;
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set portalApi(valor: PortalParceiroApi) {
    this._portalApi = valor;
  }

  set aoEnviar(valor: (solicitacao: SolicitacaoAssistencia) => void) {
    this._aoEnviar = valor;
  }

  abrir(): void {
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  connectedCallback(): void {
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._portalApi || this.enviando) return;

    const produtoDescricao = this.valorCampo('#produto');
    const problema = this.valorCampo('#problema');
    if (!produtoDescricao.trim() || !problema.trim()) {
      this.mensagemErro = 'produto e problema são obrigatórios';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const observacoes = this.valorCampo('#observacoes').trim();
      const solicitacao = await this._portalApi.solicitarAssistencia({
        produtoDescricao: produtoDescricao.trim(),
        problema: problema.trim(),
        ...(observacoes ? { observacoes } : {}),
      });
      this.aberto = false;
      this._aoEnviar?.(solicitacao);
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível enviar. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private valorCampo(seletor: string): string {
    return this.root.querySelector<HTMLInputElement | HTMLTextAreaElement>(seletor)?.value ?? '';
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        ${FORM_FIELD_CSS}
        .ajuda { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-desabilitado, #5c626b); }
        .rodape { display: flex; justify-content: flex-end; gap: var(--pg-space-3, 12px); }
        textarea { resize: vertical; min-height: 4.5rem; font-family: inherit; }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">Solicitar assistência técnica</span>
        <form novalidate>
          <label>
            Produto/controle
            <input id="produto" type="text" maxlength="200" placeholder="Ex: DualSense Edge - Drift" />
          </label>
          <label>
            Problema relatado
            <textarea id="problema" maxlength="2000" placeholder="Descreva o que está acontecendo"></textarea>
          </label>
          <label>
            Observações (opcional)
            <textarea id="observacoes" maxlength="2000"></textarea>
          </label>
          <span class="ajuda">O administrador vai analisar e definir os próximos passos.</span>
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
        </form>
        <div slot="footer" class="rodape">
          <pg-button id="botao-cancelar" variant="ghost">Cancelar</pg-button>
          <pg-button id="botao-enviar" variant="primary" ${this.enviando ? 'loading' : ''}>Enviar solicitação</pg-button>
        </div>
      </pg-modal>
    `;

    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-enviar')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-cancelar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-portal-solicitar-assistencia-modal', ErpPortalSolicitarAssistenciaModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-portal-solicitar-assistencia-modal': ErpPortalSolicitarAssistenciaModal;
  }
}
